import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useTutorialStore } from '../../store/tutorialStore';
import { useAuthStore } from '../../store/authStore';
import { TUTORIAL_STEPS, TUTORIAL_CONFIG } from '../../config/tutorialSteps';

export default function TutorialManager() {
  const location = useLocation();
  const driverObj = useRef(null);
  const { user } = useAuthStore();
  const { 
    isActive, 
    currentStep,
    shouldShow,
    checkShouldShow,
    startTutorial, 
    nextStep, 
    completeTutorial, 
    skipTutorial,
    completeStep
  } = useTutorialStore();

  // Check if tutorial should be shown when user loads
  useEffect(() => {
    if (user && shouldShow === null) {
      const show = checkShouldShow(user);
      
      // Auto-start tutorial if user hasn't completed or skipped it
      if (show) {
        // Delay slightly to ensure DOM is ready
        setTimeout(() => {
          startTutorial();
        }, 1000);
      }
    }
  }, [user, shouldShow, checkShouldShow, startTutorial]);

  // Initialize and manage driver.js
  useEffect(() => {
    if (!isActive) return;

    // Create driver instance with custom config
    driverObj.current = driver({
      ...TUTORIAL_CONFIG,
      onNextClick: () => {
        const step = TUTORIAL_STEPS[currentStep];
        if (step && step.id !== 'welcome' && step.id !== 'complete') {
          completeStep(step.id);
        }
        
        if (currentStep < TUTORIAL_STEPS.length - 1) {
          nextStep();
        } else {
          completeTutorial();
          driverObj.current?.destroy();
        }
      },
      onCloseClick: () => {
        skipTutorial();
        driverObj.current?.destroy();
      },
      onDestroyStarted: () => {
        if (driverObj.current?.isActive()) {
          skipTutorial();
        }
      }
    });

    // Highlight the current step
    const step = TUTORIAL_STEPS[currentStep];
    if (step) {
      // Determine button configuration based on step position
      const isFirstStep = currentStep === 0;
      const isLastStep = currentStep === TUTORIAL_STEPS.length - 1;
      
      const popoverConfig = {
        ...step.popover,
        showButtons: isFirstStep ? ['next', 'close'] : isLastStep ? ['previous', 'close'] : ['next', 'previous', 'close'],
        showProgress: true,
        progressText: `${currentStep + 1} of ${TUTORIAL_STEPS.length}`,
        nextBtnText: isLastStep ? 'Get Started!' : 'Next →',
        prevBtnText: '← Back',
        onNextClick: () => {
          // Mark current step as completed (except welcome step)
          if (step.id !== 'welcome') {
            completeStep(step.id);
          }
          
          if (currentStep < TUTORIAL_STEPS.length - 1) {
            nextStep();
          } else {
            // On last step, mark tutorial as completed
            completeTutorial();
            driverObj.current?.destroy();
          }
        },
        onPrevClick: () => {
          if (currentStep > 0) {
            useTutorialStore.setState({ currentStep: currentStep - 1 });
          }
        },
        onCloseClick: () => {
          // On last step, treat close as complete. On other steps, treat as skip.
          if (isLastStep) {
            completeTutorial();
          } else {
            skipTutorial();
          }
          driverObj.current?.destroy();
        }
      };
      
      // Wait a bit for elements to be available
      setTimeout(() => {
        try {
          if (step.element) {
            const element = document.querySelector(step.element);
            if (element) {
              driverObj.current?.highlight({
                element: step.element,
                popover: popoverConfig
              });
            } else {
              // Element not found, show centered popover
              driverObj.current?.highlight({
                popover: popoverConfig
              });
            }
          } else {
            // No element specified, show centered popover
            driverObj.current?.highlight({
              popover: popoverConfig
            });
          }
        } catch (error) {
          console.error('Error highlighting tutorial step:', error);
        }
      }, 300);
    }

    // Cleanup
    return () => {
      if (driverObj.current) {
        driverObj.current.destroy();
      }
    };
  }, [isActive, currentStep, nextStep, completeTutorial, skipTutorial, completeStep]);

  // Don't render anything, this component just manages the tutorial
  return null;
}
